import { PackageSearch, ScrollText } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuditLog, useStockLogs } from '@/api/hooks/useAudit';
import type { StockLogType } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDateTime } from '@/lib/utils';

const STOCK_LOG_TYPES: StockLogType[] = ['Add', 'Sale', 'Restock', 'Adjust', 'ExpireRemove', 'Return', 'PoReceive'];

function Pager({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (page: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 pt-2">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        Previous
      </Button>
      <span className="tabular text-sm text-muted-foreground">
        Page {page} of {totalPages}
      </span>
      <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
        Next
      </Button>
    </div>
  );
}

function ActivityTab({
  eventName,
  onEventChange,
  page,
  onPageChange,
}: {
  eventName: string;
  onEventChange: (v: string) => void;
  page: number;
  onPageChange: (p: number) => void;
}) {
  const debouncedEvent = useDebouncedValue(eventName, 300);
  const { data, isLoading, isFetching } = useAuditLog({ eventName: debouncedEvent || undefined, page, limit: 50 });

  const logs = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Filter by event…"
          value={eventName}
          onChange={(e) => onEventChange(e.target.value)}
          className="max-w-56"
        />
        <p className="text-sm text-muted-foreground">{data ? `${data.total} entr(ies)` : 'Loading…'}</p>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
            <ScrollText className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No matching audit entries.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1.5" aria-busy={isFetching}>
          {logs.map((entry) => (
            <div key={entry.id} className="rounded-md border border-border px-3 py-2 text-xs">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono font-medium text-foreground">{entry.event}</span>
                <span className="text-muted-foreground">{formatDateTime(entry.createdAt)}</span>
              </div>
              <p className="mt-0.5 text-muted-foreground">
                {entry.actor}
                {entry.actorRole && ` (${entry.actorRole})`}
                {entry.target && ` · target ${entry.target}`}
                {entry.ip && ` · ${entry.ip}`}
              </p>
              {entry.detail && <p className="mt-0.5 text-muted-foreground">{entry.detail}</p>}
            </div>
          ))}
        </div>
      )}

      <Pager page={page} totalPages={totalPages} onChange={onPageChange} />
    </div>
  );
}

function StockLogsTab({
  type,
  onTypeChange,
  page,
  onPageChange,
}: {
  type: string;
  onTypeChange: (v: string) => void;
  page: number;
  onPageChange: (p: number) => void;
}) {
  const { data, isLoading, isFetching } = useStockLogs({
    type: (type || undefined) as StockLogType | undefined,
    page,
    limit: 50,
  });

  const logs = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={type || 'all'} onValueChange={(v) => onTypeChange(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {STOCK_LOG_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">{data ? `${data.total} entr(ies)` : 'Loading…'}</p>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
            <PackageSearch className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No matching stock movements.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1.5" aria-busy={isFetching}>
          {logs.map((entry) => {
            const delta = entry.newStock - entry.previousStock;
            return (
              <div key={entry.id} className="rounded-md border border-border px-3 py-2 text-xs">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-foreground">{entry.medicineName ?? 'Unknown medicine'}</span>
                  <span className="text-muted-foreground">{formatDateTime(entry.createdAt)}</span>
                </div>
                <p className="mt-0.5 text-muted-foreground">
                  <span className="font-mono">{entry.type}</span> · {entry.previousStock} → {entry.newStock} (
                  {delta > 0 ? '+' : ''}
                  {delta})
                  {entry.doneByName && ` · ${entry.doneByName}`}
                  {entry.reference && ` · ref ${entry.reference}`}
                </p>
                {entry.note && <p className="mt-0.5 text-muted-foreground">{entry.note}</p>}
              </div>
            );
          })}
        </div>
      )}

      <Pager page={page} totalPages={totalPages} onChange={onPageChange} />
    </div>
  );
}

/** Who did what, inside this pharmacy — staff actions and raw stock movements. Not platform-wide (see the superadmin audit log for that). */
export function AuditLogPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'activity';
  const eventName = params.get('event') ?? '';
  const stockType = params.get('type') ?? '';
  const page = Number(params.get('page') ?? '1');

  function updateParam(key: string, value: string) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.delete('page');
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <h1 className="font-display text-3xl">Audit log</h1>

      <Tabs value={tab} onValueChange={(v) => updateParam('tab', v === 'activity' ? '' : v)}>
        <TabsList>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="stock">{t('stockLogsTitle')}</TabsTrigger>
        </TabsList>

        <TabsContent value="activity">
          <ActivityTab
            eventName={eventName}
            onEventChange={(v) => updateParam('event', v)}
            page={page}
            onPageChange={(p) => updateParam('page', String(p))}
          />
        </TabsContent>

        <TabsContent value="stock">
          <StockLogsTab
            type={stockType}
            onTypeChange={(v) => updateParam('type', v)}
            page={page}
            onPageChange={(p) => updateParam('page', String(p))}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
